import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';

const Wrapper = styled.div`
  position: relative;
`;

const ProgressBlock = styled.div`
  align-items: stretch;
  overflow: hidden;
  display: flex;
`;

const ProgressItem = styled(Vertical)`
  min-width: 0;
  flex-basis: min(
    40px,
    calc(var(--step-width-ratio) * calc(1 * var(--vw)) - calc(8 * var(--unit)))
  );
  flex-grow: 1;
  flex-shrink: 0;
  &:first-child > * {
    border-top-left-radius: var(--unit);
    border-bottom-left-radius: var(--unit);
  }
  &:last-child > * {
    border-top-right-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;

const ThresholdProgressSegment = styled(Horizontal).attrs({ gap: 0 })`
  position: relative;
  height: 40px;
  padding: var(--unit) 0 var(--half-unit) var(--half-unit);
  background: var(--competition-color);
  opacity: 0.24;
  align-self: stretch;
  overflow: hidden;
  &.completed,
  &.current {
    opacity: 1;
  }
  &.current {
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--c-nd-700);
      clip-path: inset(0 0 0 var(--completion-percent));
    }
  }
`;

const Gauge = styled.div`
  position: absolute;
  top: calc(-1 * var(--intermediate-unit));
`;

const ThresholdStatus = styled(Vertical)`
  height: 16px;
`;

type Threshold = {
  count: number;
};

type Props = {
  thresholds: Threshold[];
  effectiveIndex: number;
  liveScore: number;
};

export const RewardThresholdsGauge = ({
  thresholds,
  effectiveIndex,
  liveScore,
}: Props) => {
  const stepWidthRatio = 100 / thresholds.length;

  const currentThresholdTarget = thresholds.at(effectiveIndex)?.count || 1;
  const previousThresholdTarget =
    effectiveIndex > 0 ? thresholds.at(effectiveIndex - 1)?.count || 0 : 0;
  const completionRatio =
    (liveScore - previousThresholdTarget) /
    (currentThresholdTarget - previousThresholdTarget);

  return (
    <Wrapper>
      <ProgressBlock>
        {thresholds.map(({ count }, i) => {
          const completed = i < effectiveIndex;
          const current = i === effectiveIndex;
          return (
            <ProgressItem
              key={count}
              center
              style={{ '--step-width-ratio': stepWidthRatio } as CSSProperties}
            >
              <ThresholdProgressSegment
                className={classNames({ completed, current })}
                style={
                  current
                    ? ({
                        '--completion-percent': `${completionRatio * 100}%`,
                      } as CSSProperties)
                    : undefined
                }
              />
              <ThresholdStatus>
                {completed ? (
                  <FontAwesomeIcon
                    icon={iconCheck}
                    color="var(--c-set-main-color)"
                  />
                ) : (
                  <LabelS
                    color={!current ? 'var(--c-nd-400)' : 'var(--c-white)'}
                  >
                    {count}
                  </LabelS>
                )}
              </ThresholdStatus>
            </ProgressItem>
          );
        })}
      </ProgressBlock>
      <Gauge
        style={{
          left: `calc(${
            effectiveIndex * stepWidthRatio +
            // position gauge on the right side of the threshold
            completionRatio * stepWidthRatio
          }% - 8px)`, // 8px is half the width of the gauge, so we can position it right before the completion ratio
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="64"
          viewBox="0 0 16 64"
          fill="none"
        >
          <path
            d="M6.29086 11.3589L6.29086 56.5805C6.29086 57.3644 6.92641 58 7.7104 58C8.49439 58 9.12995 57.3645 9.12995 56.5805L9.12995 11.4032C9.12995 10.4277 9.4791 9.48434 10.1142 8.74389L10.5949 8.18357C12.6785 5.75443 10.9525 2 7.75216 2C4.60121 2 2.85862 5.654 4.84188 8.1025L5.3672 8.75106C5.96479 9.48883 6.29086 10.4094 6.29086 11.3589Z"
            fill="white"
          />
        </svg>
      </Gauge>
    </Wrapper>
  );
};
